<template>
	<div class="ventas-page">

		<div class="d-flex justify-content-between align-items-center mb-3">
			<div>
				<h1 class="mb-2">Rituals</h1>
				<p class="text-muted">{{ rows.length }} {{ rows.length === 1 ? 'ritual' : 'rituals' }}</p>
			</div>
			<div class="d-flex gap-3">
				<button class="btn btn-success" @click="openCreateModal">
					<i class="fa fa-plus"></i> Add Ritual
				</button>
			</div>
		</div>

		<div class="d-flex align-items-center gap-3 mb-4">
			<div class="d-flex align-items-center gap-2">
				<i class="fa fa-search text-muted"></i>
				<input type="text" class="form-control" v-model="searchTerm" placeholder="Search by title, author..." style="width: 320px;">
			</div>
			<select class="form-select" v-model="statusFilter" style="width: 160px;">
				<option value="">All Statuses</option>
				<option value="Published">Published</option>
				<option value="Draft">Draft</option>
			</select>
			<button class="btn btn-outline-secondary" @click="loadRows">
				<i class="fa fa-refresh"></i>
			</button>
		</div>

		<div class="ventas-card p-0">
			<div class="card-body p-0">
				<div class="table-responsive">
					<table class="table ventas-table mb-0">
						<thead>
							<tr>
								<th>Cover</th>
								<th>Title</th>
								<th>Category</th>
								<th>Author</th>
								<th>Read Time</th>
								<th>Publish Date</th>
								<th>Status</th>
								<th>Featured</th>
								<th class="text-center">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in filteredRows" :key="row.id">
								<td>
									<img v-if="row.imageUrl" :src="row.imageUrl" class="ritual-thumb" alt="">
									<div v-else class="ritual-thumb-placeholder">
										<i class="fa fa-image text-muted"></i>
									</div>
								</td>
								<td>
									<div class="font-weight-medium">{{ row.title }}</div>
									<div class="text-muted small text-truncate" style="max-width: 220px;">{{ row.excerpt }}</div>
								</td>
								<td class="text-muted">{{ row.category || '—' }}</td>
								<td>{{ row.author || '—' }}</td>
								<td class="text-muted">{{ row.read_time || '—' }}</td>
								<td class="text-muted">{{ formatDate(row.publish_date) }}</td>
								<td>
									<span class="badge" :class="row.status === 'Published' ? 'badge-success' : 'badge'">
										{{ row.status }}
									</span>
								</td>
								<td>
									<span v-if="row.featured" class="badge badge-info">Featured</span>
									<span v-else class="text-muted">—</span>
								</td>
								<td class="text-center">
									<button class="btn btn-sm btn-outline-secondary border-0" @click="openEditModal(row)">
										<i class="fa fa-edit"></i>
									</button>
									<button class="btn btn-sm btn-outline-danger border-0" @click="deleteRow(row.id)">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
							<tr v-if="filteredRows.length === 0">
								<td colspan="9" class="text-center py-4">
									<div class="text-muted">
										<i class="fa fa-book-open fa-2x mb-2"></i>
										<p>No rituals found</p>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Modal Add / Edit Ritual -->
		<div class="modal fade" id="ritualModal" tabindex="-1">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">{{ form.id ? 'Edit Ritual' : 'New Ritual' }}</h5>
						<button type="button" class="btn btn-xs" @click="closeModal">
							<i class="fa fa-times"></i>
						</button>
					</div>
					<div class="modal-body">
						<form id="ritualForm" @submit.prevent="saveRow">
							<div class="form-group mb-3">
								<label class="control-label">Title *</label>
								<input type="text" class="form-control" v-model="form.title" placeholder="Article title" required>
							</div>

							<div class="form-group mb-3">
								<label class="control-label">Excerpt</label>
								<textarea class="form-control" v-model="form.excerpt" rows="2" placeholder="Short summary..."></textarea>
							</div>

							<div class="row">
								<div class="col-md-6 mb-3">
									<label class="control-label">Category</label>
									<v-select
										v-model="form.category"
										name="category"
										:options="categoryOptions"
										placeholder="Select category"
										index="name"
										label="name"
									></v-select>
								</div>
								<div class="col-md-6 mb-3">
									<label class="control-label">Author</label>
									<input type="text" class="form-control" v-model="form.author" placeholder="Author name">
								</div>
							</div>

							<div class="row">
								<div class="col-md-4 mb-3">
									<label class="control-label">Read Time</label>
									<input type="text" class="form-control" v-model="form.read_time" placeholder="e.g. 8 min">
								</div>
								<div class="col-md-4 mb-3">
									<label class="control-label">Publish Date</label>
									<input type="date" class="form-control" v-model="form.publish_date">
								</div>
								<div class="col-md-4 mb-3">
									<label class="control-label">Status</label>
									<v-select
										v-model="form.status"
										name="status"
										:options="statusOptions"
										placeholder="Select status"
										index="name"
										label="name"
									></v-select>
								</div>
							</div>

							<div class="form-group mb-3">
								<div class="form-check">
									<input type="checkbox" class="form-check-input" id="ritualFeatured" v-model="form.featured">
									<label class="form-check-label" for="ritualFeatured">Mark as featured</label>
								</div>
							</div>

							<div class="form-group mb-3">
								<label class="control-label">Cover Image</label>
								<div class="ritual-photo">
									<label class="ritual-photo__preview" for="ritualImage">
										<img v-if="photoPreview" :src="photoPreview" alt="Cover">
										<span v-else class="ritual-photo__placeholder">
											<i class="fa fa-image"></i>
											<small>Upload cover</small>
										</span>
									</label>
									<input
										id="ritualImage"
										ref="imageInput"
										type="file"
										accept="image/*"
										name="image"
										class="d-none"
										@change="onImageChange"
									>
									<div class="ritual-photo__actions">
										<label for="ritualImage" class="btn btn-sm btn-outline-secondary mb-0">
											{{ photoPreview ? 'Change' : 'Upload' }}
										</label>
										<button
											v-if="photoPreview"
											type="button"
											class="btn btn-sm btn-outline-danger"
											@click="clearImage"
										>
											Remove
										</button>
									</div>
								</div>
							</div>

							<div class="form-group mb-0 ritual-body-editor">
								<label class="control-label mb-2">Article Body</label>
								<vue-editor
									id="ritualBodyEditor"
									v-model="form.body"
									:editorToolbar="customToolbar"
									:editorOptions="editorOptions"
								></vue-editor>
							</div>
						</form>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-success" @click="saveRow">
							{{ form.id ? 'Update Ritual' : 'Create Ritual' }}
						</button>
					</div>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
import { VueEditor } from 'vue2-editor'

export default {
	components: {
		VueEditor
	},
	data() {
		return {
			rows: [],
			searchTerm: '',
			statusFilter: '',
			localPreview: null,
			categoryOptions: [
				{ name: 'Morning' },
				{ name: 'Evening' },
				{ name: 'Night' },
				{ name: 'Seasonal' },
				{ name: 'Wellness' }
			],
			statusOptions: [
				{ name: 'Draft' },
				{ name: 'Published' }
			],
			customToolbar: [
				[{ header: [false, 1, 2, 3, 4, 5, 6] }],
				[{ font: [] }],
				['bold', 'italic', 'underline', 'strike'],
				[{ color: [] }, { background: [] }],
				[{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
				[{ list: 'ordered' }, { list: 'bullet' }],
				[{ indent: '-1' }, { indent: '+1' }],
				['blockquote', 'code-block'],
				['link', 'image'],
				['clean']
			],
			editorOptions: {
				placeholder: 'Full article content...'
			},
			form: {
				id: null,
				title: '',
				excerpt: '',
				category: 'Morning',
				author: '',
				read_time: '',
				publish_date: '',
				status: 'Draft',
				featured: false,
				body: '',
				imageUrl: null
			}
		}
	},
	computed: {
		filteredRows() {
			let filtered = this.rows

			if (this.searchTerm) {
				const search = this.searchTerm.toLowerCase()
				filtered = filtered.filter(row =>
					(row.title && row.title.toLowerCase().includes(search)) ||
					(row.author && row.author.toLowerCase().includes(search)) ||
					(row.category && row.category.toLowerCase().includes(search)) ||
					(row.excerpt && row.excerpt.toLowerCase().includes(search))
				)
			}

			if (this.statusFilter) {
				filtered = filtered.filter(row => row.status === this.statusFilter)
			}

			return filtered
		},
		photoPreview() {
			return this.localPreview || this.form.imageUrl || null
		}
	},
	methods: {
		formatDate(value) {
			if (!value) return '—'
			const d = new Date(value)
			if (isNaN(d.getTime())) return value
			return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		},
		loadRows() {
			this.$root.isLoading = true
			axios.get(tools.url('/api/admin/' + this.$route.meta.path)).then((response) => {
				this.rows = response.data
				this.$root.isLoading = false
			}).catch((error) => {
				this.$root.handleErrors(error)
				this.$root.isLoading = false
			})
		},
		onImageChange(e) {
			const file = e.target.files && e.target.files[0]
			if (!file) {
				this.localPreview = null
				return
			}
			if (this.localPreview && this.localPreview.indexOf('blob:') === 0) {
				URL.revokeObjectURL(this.localPreview)
			}
			this.localPreview = URL.createObjectURL(file)
		},
		clearImage() {
			if (this.localPreview && this.localPreview.indexOf('blob:') === 0) {
				URL.revokeObjectURL(this.localPreview)
			}
			this.localPreview = null
			this.form.imageUrl = null
			if (this.$refs.imageInput) {
				this.$refs.imageInput.value = ''
			}
		},
		resetForm() {
			this.localPreview = null
			if (this.$refs.imageInput) {
				this.$refs.imageInput.value = ''
			}
			this.form = {
				id: null,
				title: '',
				excerpt: '',
				category: 'Morning',
				author: '',
				read_time: '',
				publish_date: '',
				status: 'Draft',
				featured: false,
				body: '',
				imageUrl: null
			}
		},
		closeModal() {
			$('#ritualModal').modal('hide')
			this.resetForm()
		},
		openCreateModal() {
			this.resetForm()
			$('#ritualModal').modal('show')
		},
		openEditModal(row) {
			this.localPreview = null
			if (this.$refs.imageInput) {
				this.$refs.imageInput.value = ''
			}
			this.form = {
				id: row.id,
				title: row.title || '',
				excerpt: row.excerpt || '',
				category: row.category || 'Morning',
				author: row.author || '',
				read_time: row.read_time || '',
				publish_date: row.publish_date ? String(row.publish_date).substring(0, 10) : '',
				status: row.status || 'Draft',
				featured: !!row.featured,
				body: row.body || '',
				imageUrl: row.imageUrl || null
			}
			$('#ritualModal').modal('show')
		},
		saveRow() {
			if (!this.form.title) {
				this.$root.showMessage('Title is required', 'error')
				return
			}

			this.$root.isLoading = true
			const payload = {
				title: this.form.title,
				excerpt: this.form.excerpt,
				category: this.form.category,
				author: this.form.author,
				read_time: this.form.read_time,
				publish_date: this.form.publish_date,
				status: this.form.status,
				featured: this.form.featured ? 1 : 0,
				body: this.form.body
			}
			var data = tools.params(document.getElementById('ritualForm'), payload)

			var request
			if (this.form.id) {
				request = axios.post(tools.url('/api/admin/' + this.$route.meta.path + '/' + this.form.id), data)
			} else {
				request = axios.post(tools.url('/api/admin/' + this.$route.meta.path), data)
			}

			request.then((response) => {
				this.$root.showMessage(response.data.msg || 'Saved successfully', 'success')
				this.$root.isLoading = false
				$('#ritualModal').modal('hide')
				this.resetForm()
				this.loadRows()
			}).catch((error) => {
				this.$root.isLoading = false
				this.$root.handleErrors(error)
			})
		},
		deleteRow(id) {
			this.$swal({
				title: 'Are you sure you want to delete this ritual?',
				text: 'You will not be able to revert this!',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'Yes, delete',
				cancelButtonText: 'Cancel',
				customClass: {
					popup: 'custom-toast'
				},
			}).then((result) => {
				if (result.isConfirmed) {
					axios.delete(tools.url('/api/admin/' + this.$route.meta.path + '/' + id))
						.then((response) => {
							this.$root.showMessage(response.data.msg, 'success')
							this.loadRows()
						})
						.catch((error) => {
							this.$root.handleErrors(error)
						})
				}
			})
		}
	},
	mounted() {
		this.loadRows()
	}
}
</script>

<style scoped>
.ritual-thumb {
	width: 56px;
	height: 40px;
	object-fit: cover;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
}
.ritual-thumb-placeholder {
	width: 56px;
	height: 40px;
	border-radius: 6px;
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	display: flex;
	align-items: center;
	justify-content: center;
}
.ritual-photo {
	display: flex;
	flex-direction: column;
	gap: 10px;
}
.ritual-photo__preview {
	width: 100%;
	height: 140px;
	border-radius: 10px;
	overflow: hidden;
	border: 2px dashed #d1d5db;
	background: #f3f4f6;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0;
	cursor: pointer;
}
.ritual-photo__preview:hover {
	border-color: #9ca3af;
}
.ritual-photo__preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}
.ritual-photo__placeholder {
	color: #9ca3af;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	font-size: 28px;
}
.ritual-photo__placeholder small {
	font-size: 12px;
}
.ritual-photo__actions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.ritual-body-editor >>> .quillWrapper {
	border-radius: 10px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
}

.ritual-body-editor >>> .ql-toolbar.ql-snow {
	border: none;
	border-bottom: 1px solid #e5e7eb;
	background: #f8f9fb;
	padding: 10px 12px;
}

.ritual-body-editor >>> .ql-container.ql-snow {
	border: none;
	min-height: 220px;
	font-size: 15px;
	line-height: 1.65;
}

.ritual-body-editor >>> .ql-editor {
	min-height: 220px;
	padding: 16px 18px;
}

.ritual-body-editor >>> .ql-editor.ql-blank::before {
	color: #9ca3af;
	font-style: normal;
}
</style>
